import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Building2, CalendarDays, Clock, Briefcase, User } from 'lucide-react';
import { formatDate } from '../../utils/dateFormat';

const ReferentContratsView = ({ contrats, entreprises, beneficiaires }) => {
  // Filtrer les contrats des bénéficiaires du référent
  const mesBeneficiairesIds = beneficiaires.map(b => b.id);
  const mesContrats = contrats.filter(c => mesBeneficiairesIds.includes(c.apprenant_id));

  const getEntrepriseName = (entrepriseId) => {
    const entreprise = entreprises.find(e => e.id === entrepriseId);
    return entreprise?.nom || 'Entreprise inconnue';
  };

  const getBeneficiaireName = (apprenantId) => {
    const benef = beneficiaires.find(b => b.id === apprenantId);
    return benef ? `${benef.prenom} ${benef.nom}` : 'Bénéficiaire inconnu';
  };

  const getStatusBadge = (statut) => {
    const styles = {
      'en_cours': 'bg-emerald-100 text-emerald-700',
      'termine': 'bg-slate-100 text-slate-700',
      'rompu': 'bg-red-100 text-red-700'
    };
    const labels = {
      'en_cours': 'En cours',
      'termine': 'Terminé',
      'rompu': 'Rompu'
    };
    return (
      <Badge className={styles[statut] || 'bg-slate-100 text-slate-700'}>
        {labels[statut] || statut}
      </Badge>
    );
  };

  return (
    <Card className="bg-white border-slate-200 rounded-3xl shadow-sm">
      <CardHeader>
        <CardTitle className="text-xl font-bold" style={{ fontFamily: 'Nunito, sans-serif' }}>
          Contrats signés par mes bénéficiaires
        </CardTitle>
      </CardHeader>
      <CardContent>
        {mesContrats.length === 0 ? (
          <div className="text-center py-12">
            <Briefcase className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Aucun contrat enregistré</p>
          </div>
        ) : (
          <div className="space-y-4">
            {mesContrats.map((contrat) => (
              <Card key={contrat.id} className="border border-slate-200 rounded-2xl overflow-hidden">
                <div className="p-3 sm:p-4">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <User className="w-4 h-4 text-blue-600 flex-shrink-0" />
                        <span className="font-bold text-slate-900 text-sm sm:text-base">
                          {getBeneficiaireName(contrat.apprenant_id)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Building2 className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        <span className="truncate">{getEntrepriseName(contrat.entreprise_id)}</span>
                      </div>
                    </div>
                    <div className="flex flex-row sm:flex-col items-start sm:items-end gap-2 flex-shrink-0">
                      <Badge className={`${
                        contrat.type_contrat === 'CDI' ? 'bg-emerald-100 text-emerald-700' :
                        contrat.type_contrat === 'CDD' ? 'bg-blue-100 text-blue-700' :
                        'bg-amber-100 text-amber-700'
                      }`}>
                        {contrat.type_contrat}
                      </Badge>
                      {getStatusBadge(contrat.statut)}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3 text-xs sm:text-sm">
                    {contrat.poste && (
                      <div className="flex items-center gap-1 text-slate-600">
                        <Briefcase className="w-3.5 h-3.5 text-slate-400" />
                        {contrat.poste}
                      </div>
                    )}
                    {contrat.date_debut && (
                      <div className="flex items-center gap-1 text-slate-600">
                        <CalendarDays className="w-3.5 h-3.5 text-slate-400" />
                        Début: {formatDate(contrat.date_debut)}
                      </div>
                    )}
                    {contrat.date_fin && (
                      <div className="flex items-center gap-1 text-slate-600">
                        <CalendarDays className="w-3.5 h-3.5 text-slate-400" />
                        Fin: {formatDate(contrat.date_fin)}
                      </div>
                    )}
                    {contrat.salaire && (
                      <div className="flex items-center gap-1 text-slate-600">
                        <span className="text-slate-400">€</span>
                        {contrat.salaire} €/mois
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ReferentContratsView;
